import styled from 'styled-components';
import { Avatar, Flex, Spacer, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Participant } from '@feature/cases/common';
import { withTranslation } from '@application/i18n/with_translation';
import { DateService, DateDisplayFormats } from '@application/time/date.service';
import { useService } from '@visiba/instantiation';

interface Props {
	host: Participant | null;
	participantAmount: number;
	receptionName: string;
	time?: VisibaDate;
	rfv?: string;
	representativeNiN?: string;
}

const elements = {
	container: styled.div`
		display: flex;
	`,
	avatar: styled.div`
		display: flex;
		align-items: flex-start;
		width: 48px;
		flex-shrink: 0;
	`,
	textSections: styled.div`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x1_5};
		max-width: 100%;
		width: 100%;

		${tokens.breakpointQuery.medium} {
			max-width: 70%;
		}
	`,
	textSection: styled.div`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x0_5};
		max-width: 75%;
	`,
};

/**
 * Information about the case in written form.
 */
export function ExtensiveInfo({ host, receptionName, representativeNiN, rfv, time, participantAmount }: Props): JSX.Element {
	const dateService = useService(DateService);
	const formattedDate = time?.format(
		dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_IN_CURRENT_YEAR_NO_WEEKDAY),
	);

	return (
		<elements.container>
			<elements.avatar>
				{participantAmount > 1 ? (
					<Avatar icon='group' size='large' />
				) : (
					<Avatar src={host?.profileImageUrl} name={host?.name ?? receptionName ?? ''} size='large' />
				)}
			</elements.avatar>

			<Spacer horizontal={tokens.spacing.x2} />

			{host != null ? (
				<elements.textSections>
					<TextSectionOne
						host={host}
						participantAmount={participantAmount}
						receptionName={receptionName}
						formattedDate={formattedDate}
					/>
					<TextSectionTwo representativeNiN={representativeNiN} rfv={rfv} />
				</elements.textSections>
			) : (
				<Flex>
					<Text
						variant='headingXSmall'
						style={{
							color: tokens.color.content.default,
						}}
					>
						{receptionName}
					</Text>
				</Flex>
			)}
		</elements.container>
	);
}

interface TextSectionOneProps {
	host: Participant;
	receptionName: string;
	participantAmount: number;
	formattedDate?: string;
}

function TextSectionOne({ host, receptionName, formattedDate, participantAmount }: TextSectionOneProps): JSX.Element {
	return (
		<elements.textSection>
			<Text variant='headingXSmall' style={{ color: tokens.color.content.default }}>
				{host.name} {participantAmount > 1 ? <span>+{participantAmount - 1 /* -1 because host is not counted */}</span> : null}
			</Text>
			<Text variant='labelRegular' style={{ color: tokens.color.content.default }}>
				{receptionName}
			</Text>
			{formattedDate != null ? (
				<Text variant='labelSemiBold' style={{ color: tokens.color.content.default }}>
					{formattedDate}
				</Text>
			) : null}
		</elements.textSection>
	);
}

interface TextSectionTwoProps {
	representativeNiN?: string;
	rfv?: string;
}

function TextSectionTwo({ representativeNiN, rfv }: TextSectionTwoProps): JSX.Element | null {
	if (representativeNiN == null && rfv == null) return null;

	return (
		<elements.textSection>
			{rfv != null ? (
				<Text variant='labelRegular' style={{ color: tokens.color.content.muted }}>
					{rfv}
				</Text>
			) : null}
			{representativeNiN != null ? (
				<Text variant='labelRegular' style={{ color: tokens.color.content.muted }}>
					<Text variant='labelSemiBold'>{withTranslation('PW.Component.CaseCard.AgentFor')}</Text> {representativeNiN}
				</Text>
			) : null}
		</elements.textSection>
	);
}
